import React, { useState } from "react";
import classnames from "classnames";

interface ContextualBarProps {
  className?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  placeholder?: string;
  showSearch?: boolean;
  searchDisabled?: boolean;
  showFilters?: boolean;
  filtersLabel?: string;
  onFiltersClick?: () => void;
  cta?: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
  };
  leftExtras?: React.ReactNode;
  rightExtras?: React.ReactNode;
}

// Search Icon Component
function SearchIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      className={className}
    >
      <path
        fillRule="evenodd"
        d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z"
        clipRule="evenodd"
      />
    </svg>
  );
}

// Editorial Search Button (renamed from Filter)
function EditorialSearchButton({
  onClick,
  children,
}: {
  onClick?: () => void;
  children: React.ReactNode;
}) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="h-11 px-4 rounded-lg text-sm font-semibold transition-all flex items-center gap-2"
      style={{
        fontFamily: "'Source Sans 3', sans-serif",
        background: isHovered ? '#C2410C' : 'rgba(194, 65, 12, 0.08)',
        border: '1.5px solid #C2410C',
        color: isHovered ? '#FFFEF9' : '#C2410C',
        boxShadow: isHovered ? '0 4px 12px -2px rgba(194, 65, 12, 0.3)' : 'none',
        transform: isHovered ? 'translateY(-1px)' : 'translateY(0)',
      }}
    >
      <SearchIcon className="w-4 h-4" />
      {children}
    </button>
  );
}

// Editorial CTA Button
function EditorialCTAButton({
  onClick,
  disabled,
  children,
}: {
  onClick?: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="h-11 px-5 rounded-lg text-sm font-semibold text-white transition-all"
      style={{
        fontFamily: "'Source Sans 3', sans-serif",
        background: disabled ? '#A8A29E' : (isHovered ? '#A13508' : '#C2410C'),
        boxShadow: disabled ? 'none' : (isHovered ? '0 6px 16px -2px rgba(194, 65, 12, 0.4)' : '0 4px 12px -2px rgba(194, 65, 12, 0.3)'),
        transform: isHovered && !disabled ? 'translateY(-1px)' : 'translateY(0)',
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      {children}
    </button>
  );
}

function ContextualBar({
  className,
  searchValue = "",
  onSearchChange,
  placeholder = "Search by keyword...",
  showSearch = true,
  searchDisabled,
  showFilters,
  filtersLabel = "Search",
  onFiltersClick,
  cta,
  leftExtras,
  rightExtras,
}: ContextualBarProps) {
  const shouldRenderSearch = showSearch && typeof onSearchChange === "function";

  const hasLeftContent = shouldRenderSearch || !!leftExtras;

  const filterButton =
    showFilters && (
      <EditorialSearchButton onClick={onFiltersClick}>
        {filtersLabel}
      </EditorialSearchButton>
    );

  const showFilterOnLeft = hasLeftContent;

  return (
    <div
      className={classnames("relative z-20 border-b", className)}
      style={{ borderColor: '#E7E5E4' }}
    >
      <div className="flex w-full flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:gap-4 sm:px-6 lg:px-8">
        <div
          className={classnames("flex flex-wrap items-center gap-3", {
            "flex-1": hasLeftContent,
          })}
        >
          {shouldRenderSearch && (
            <div className="relative flex-1 min-w-[200px]">
              <input
                value={searchValue}
                onChange={(event) => onSearchChange?.(event.target.value)}
                placeholder={placeholder}
                disabled={searchDisabled}
                className="h-11 w-full rounded-lg px-3 text-sm transition-all disabled:cursor-not-allowed"
                style={{
                  fontFamily: "'Source Sans 3', sans-serif",
                  background: '#FFFEF9',
                  border: '1px solid #E7E5E4',
                  color: '#1C1917',
                  outline: 'none',
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#C2410C';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(194, 65, 12, 0.1)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#E7E5E4';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
            </div>
          )}
          {showFilterOnLeft && filterButton}
          {showFilterOnLeft && leftExtras}
        </div>

        <div className="flex items-center gap-3 sm:justify-end sm:ml-auto">
          {!showFilterOnLeft && filterButton}
          {rightExtras}
          {cta && (
            <EditorialCTAButton
              onClick={cta.onClick}
              disabled={cta.disabled}
            >
              {cta.label}
            </EditorialCTAButton>
          )}
        </div>
      </div>
    </div>
  );
}

export default ContextualBar;
